import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Box, Grid, Skeleton, Alert,
  Chip, useTheme, useMediaQuery,
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import MetricCard from '../components/shared/MetricCard';
import MobileStockCard from '../components/shared/MobileStockCard';
import PnLChart from '../components/charts/PnLChart';
import { formatCurrency, formatPercent, getPnLColor } from '../utils/formatters';

const PnL = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchPnL = async () => {
      try {
        const response = await axios.get('/pnl');
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch P&L data');
      } finally {
        setLoading(false);
      }
    };
    fetchPnL();
  }, []);

  if (loading) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2.5 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={220} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const { trades, summary, cumulativePnL } = data;
  const totalPnl = parseFloat(summary.totalRealizedPnl);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Profit & Loss</Typography>

      {/* Summary MetricCards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Total Realized P&L"
            value={formatCurrency(totalPnl)}
            color={getPnLColor(totalPnl)}
            trend={summary.overallPnlPercent ? { value: totalPnl, label: formatPercent(summary.overallPnlPercent) } : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Total Trades" value={summary.totalTrades} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Total Cost" value={formatCurrency(parseFloat(summary.totalCost))} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Total Proceeds" value={formatCurrency(parseFloat(summary.totalProceeds))} />
        </Grid>
      </Grid>

      {/* Cumulative P&L Chart */}
      {cumulativePnL && cumulativePnL.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Cumulative Realized P&L
            </Typography>
            <PnLChart data={cumulativePnL} />
          </CardContent>
        </Card>
      )}

      {/* Trades Table */}
      {trades.length === 0 ? (
        <Alert severity="info">No completed trades yet.</Alert>
      ) : isMobile ? (
        /* Mobile: Card layout */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {trades.map((trade) => {
            const pnl = parseFloat(trade.realized_pnl);
            return (
              <MobileStockCard
                key={trade.id}
                symbol={trade.symbol}
                shares={trade.shares_sold}
                avgPrice={parseFloat(trade.cost_basis_per_share)}
                currentPrice={parseFloat(trade.sell_price_per_share)}
                gainLoss={pnl}
                gainLossPercent={parseFloat(trade.pnl_percent)}
                orderDate={trade.trade_date}
                onClick={() => window.location.href = `/stock/${trade.symbol}`}
              />
            );
          })}
        </Box>
      ) : (
        /* Desktop: Table layout */
        <TableContainer component={Paper}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell align="right">Shares</TableCell>
                <TableCell align="right">Cost Basis</TableCell>
                <TableCell align="right">Sell Price</TableCell>
                <TableCell align="right">Total Cost</TableCell>
                <TableCell align="right">Proceeds</TableCell>
                <TableCell align="right">P&L</TableCell>
                <TableCell align="right">P&L %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades.map((trade) => {
                const pnl = parseFloat(trade.realized_pnl);
                const isProfit = pnl >= 0;
                return (
                  <TableRow key={trade.id}>
                    <TableCell>{new Date(trade.trade_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <strong>
                        <RouterLink
                          to={`/stock/${trade.symbol}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.target.style.color = '#1976d2'}
                          onMouseLeave={(e) => e.target.style.color = 'inherit'}
                        >
                          {trade.symbol}
                        </RouterLink>
                      </strong>
                    </TableCell>
                    <TableCell align="right">{parseFloat(trade.shares_sold)}</TableCell>
                    <TableCell align="right">${parseFloat(trade.cost_basis_per_share).toFixed(2)}</TableCell>
                    <TableCell align="right">${parseFloat(trade.sell_price_per_share).toFixed(2)}</TableCell>
                    <TableCell align="right">${parseFloat(trade.total_cost).toLocaleString()}</TableCell>
                    <TableCell align="right">${parseFloat(trade.total_proceeds).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        icon={isProfit ? <TrendingUp /> : <TrendingDown />}
                        label={`${isProfit ? '+' : ''}$${pnl.toFixed(2)}`}
                        color={isProfit ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ color: isProfit ? 'success.main' : 'error.main' }}>
                      {isProfit ? '+' : ''}{parseFloat(trade.pnl_percent).toFixed(2)}%
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PnL;
